import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { SlideOutWrapper } from './SlideOutWrapper';
import { ProgramsAndIncentivesCompactUI } from '@makemydeal/dr-activities-programs';
import { UnsavedChangesConfirmationModal, useUnsavedChangesModal } from '@makemydeal/dr-dash-components';
import type { OnCloseOptions } from '@makemydeal/dr-dash-components';
import * as manualIncentivesSelectors from '@makemydeal/dr-dash-store/selectors/manualIncentivesSelectors';
import * as bootstrapSelectors from '@makemydeal/dr-dash-store/selectors/bootstrapSelectors';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { FULL_SCREEN_SLIDE_OUT_WIDTH, getReturnToOfferButton } from '@makemydeal/dr-shared-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { SlideOutComponentProps } from '../../types';

const UNSAVED_INCENTIVES_MESSAGE =
    "You've made incentive updates that haven't been applied. Cancel Changes will discard them permanently. Continue Editing to go back and keep working.";

export const IncentivesSlideOut = ({ isOpen, onClose }: SlideOutComponentProps) => {
    const dispatch = useDispatch();

    // Use isEqual for deep comparison to prevent unnecessary re-renders when arrays have same content
    const manualIncentives = useSelector(manualIncentivesSelectors.getCurrentEditedNonDeletedIncentives, isEqual);
    const selectedIncentives = useSelector(programsSelectors.getSelectedIncentives, isEqual);
    const appliedIncentives = useSelector(offerReduxSelectors.getAppliedIncentives, isEqual);
    const dealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForDealerCash);
    const nonDealerCashTotal = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash);
    const initSuccessCount = useSelector(bootstrapSelectors.getInitSuccessCount);
    const restorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);

    // Snapshot incentives each time the slide-out opens so cancel can revert to the correct prior state.
    // useRef alone would freeze the snapshot at initial mount; updating on isOpen=true handles re-opens.
    const openSnapshotRef = useRef({ incentives: appliedIncentives, dealerCashTotal, nonDealerCashTotal });
    useEffect(() => {
        if (isOpen) {
            openSnapshotRef.current = { incentives: appliedIncentives, dealerCashTotal, nonDealerCashTotal };
        }
    }, [isOpen]);

    // Sync after each successful init (INIT_SUCCESS / INIT_PENCIL_SUCCESS) to ensure Redux has
    // the latest penciled incentives. Only needed for compact UI where the slide-out manages the incentives editing flow.
    useEffect(() => {
        if (restorationEnabled) {
            return;
        }
        if (appliedIncentives.length > 0) {
            dispatch(
                offerActionCreators.updateManualIncentives({
                    manualIncentives: appliedIncentives,
                    dealerCashTotal,
                    totalRebates: nonDealerCashTotal,
                    skipPayment: true,
                    preserveSelectedIncentives: true
                })
            );
        }
    }, [initSuccessCount]);

    // On cancel, revert Redux incentives to the snapshot taken at open so that
    // manually-added incentives that were never saved are not persisted.
    const onCloseWithRevert = useCallback(
        (options?: OnCloseOptions) => {
            if (options?.cancelled) {
                const {
                    incentives,
                    dealerCashTotal: snapDealerCash,
                    nonDealerCashTotal: snapNonDealerCash
                } = openSnapshotRef.current;
                dispatch(
                    offerActionCreators.updateManualIncentives({
                        manualIncentives: incentives,
                        dealerCashTotal: snapDealerCash,
                        totalRebates: snapNonDealerCash,
                        skipPayment: true,
                        ...(restorationEnabled && { preserveSelectedIncentives: true })
                    })
                );
            }
            onClose(options);
        },
        [dispatch, onClose, restorationEnabled]
    );

    const incentivesData = useMemo(() => ({ manualIncentives, selectedIncentives }), [manualIncentives, selectedIncentives]);

    const {
        isDirtyRef: isIncentivesFormDirtyRef,
        showConfirmationModal,
        handleClose,
        handleCancelChanges,
        handleContinueEditing
    } = useUnsavedChangesModal({
        savedData: incentivesData,
        onClose: onCloseWithRevert
    });

    return (
        <>
            <SlideOutWrapper
                title={'Programs & Incentives'}
                panelWidth={FULL_SCREEN_SLIDE_OUT_WIDTH}
                isOpen={isOpen}
                onClose={handleClose}
                testId="tab-slideout-incentives"
                footer={getReturnToOfferButton(handleClose)}
            >
                <ProgramsAndIncentivesCompactUI
                    isIncentivesFormDirtyRef={isIncentivesFormDirtyRef}
                    showConfirmationModal={showConfirmationModal}
                />
            </SlideOutWrapper>
            <UnsavedChangesConfirmationModal
                show={showConfirmationModal}
                onCancelChanges={handleCancelChanges}
                onContinueEditing={handleContinueEditing}
                message={UNSAVED_INCENTIVES_MESSAGE}
            />
        </>
    );
};
